/**
 * YUMIX — StatTile : reusable GLOW-mode KPI tile (Iter. 388).
 *
 * Drop-in replacement for any flat `<div>{icon}{value}{label}</div>` numeric
 * KPI used across the app. Matches the visual signature of charts
 * (`GlowChartDefs`) and the FastingWidget eating-window pulse.
 *
 * Usage:
 *   <StatTile theme="red" icon={Flame} value={42} label="cal burned" />
 *   <StatTile theme="emerald" icon={Activity} value="2300" label="kcal target" align="left" />
 *
 * Themes (matches GlowChartDefs): red/blue/emerald/orange/mauve/cyan/amber/rose
 * Falls back to red if an unknown theme is passed.
 */
import React from 'react';

const THEMES = {
  red:     { rgb: '248,113,113', text: 'text-red-300',     bg: 'rgba(248,113,113,', border: 'rgba(248,113,113,0.30)' },
  blue:    { rgb: '96,165,250',  text: 'text-blue-300',    bg: 'rgba(96,165,250,',  border: 'rgba(96,165,250,0.30)'  },
  emerald: { rgb: '52,211,153',  text: 'text-emerald-300', bg: 'rgba(52,211,153,',  border: 'rgba(52,211,153,0.30)'  },
  orange:  { rgb: '251,146,60',  text: 'text-orange-300',  bg: 'rgba(251,146,60,',  border: 'rgba(251,146,60,0.30)'  },
  mauve:   { rgb: '168,85,247',  text: 'text-purple-300',  bg: 'rgba(168,85,247,',  border: 'rgba(168,85,247,0.30)'  },
  cyan:    { rgb: '34,211,238',  text: 'text-cyan-300',    bg: 'rgba(34,211,238,',  border: 'rgba(34,211,238,0.30)'  },
  amber:   { rgb: '245,158,11',  text: 'text-amber-300',   bg: 'rgba(245,158,11,',  border: 'rgba(245,158,11,0.30)'  },
  rose:    { rgb: '244,114,182', text: 'text-pink-300',    bg: 'rgba(244,114,182,', border: 'rgba(244,114,182,0.30)' },
};

const StatTile = ({
  theme = 'emerald',
  icon: Icon,
  value,
  label,
  hint,
  align = 'center',
  compact = false,
  className = '',
  ...rest
}) => {
  const t = THEMES[theme] || THEMES.emerald;
  const padding = compact ? 'p-2' : 'p-2.5';
  const valSize = compact ? 'text-xl' : 'text-2xl';

  return (
    <div
      className={`${padding} rounded-xl border ${className}`}
      style={{
        borderColor: t.border,
        background: `linear-gradient(135deg, ${t.bg}0.10), ${t.bg}0.02))`,
        boxShadow: `0 0 14px ${t.bg}0.20), inset 0 0 0 1px ${t.bg}0.10)`,
        textAlign: align,
      }}
      {...rest}
    >
      {Icon && (
        <Icon
          className={`w-5 h-5 ${t.text} ${align === 'center' ? 'mx-auto' : ''} mb-1`}
          style={{ filter: `drop-shadow(0 0 6px rgba(${t.rgb},0.75))` }}
        />
      )}
      <p
        className={`font-heading ${valSize} font-black ${t.text} tabular-nums leading-none`}
        style={{ textShadow: `0 0 12px rgba(${t.rgb},0.55)` }}
      >
        {value}
      </p>
      {label && (
        <p className="text-[10px] text-muted-foreground uppercase mt-1 leading-tight">{label}</p>
      )}
      {hint && (
        <p className="text-[9px] text-muted-foreground/60 mt-0.5 leading-tight">{hint}</p>
      )}
    </div>
  );
};

export default StatTile;
